import { useCallback, useMemo } from 'react';

import {
  APPLICATION_ERROR_TIN_ALREADY_REGISTERED,
  getRedirectEventForSection,
  mapPartnerApplicationContextToApplicationData,
  mapPartnerApplicationContextToCreateApplicationData,
  PartnerApplicationMachineContext,
  PartnerApplicationResponse,
  partnerApplicationKeys,
  useCreatePartnerApplication,
  useSubmitPartnerApplication,
  useUpdatePartnerApplication,
  validateAndGetFirstInvalidSection,
  validationRedirectMessage,
} from '~domains/partner-application/entities/application';
import { pushApplicationIdToUrl } from '~domains/partner-application/shared/lib';

import { ampli } from '~shared/analytics';
import { createSilentError } from '~shared/lib/create-silent-error';
import { useConfirmation } from '~shared/ui/confirmation';

import { toast } from '@breezeventures/ui-kit/toast';
import { isHttpError } from '@breezeventures/web-lib-ph-request/errors';
import { formatHttpErrorMessage } from '@breezeventures/web-lib-ph-request/utils';
import { useQueryClient } from '@tanstack/react-query';

type UpdateApplicationFn = (params?: {
  onError?: () => void;
}) => Promise<PartnerApplicationResponse>;

type UseApplicationActionsValue = {
  createOrUpdateApplication: () => Promise<PartnerApplicationResponse>;
  updateApplication: UpdateApplicationFn;
  submitApplication: () => Promise<PartnerApplicationResponse>;
};

const UPDATE_FAILED_MESSAGE = 'Failed to update application.';
const CREATE_FAILED_MESSAGE = 'Failed to create application.';
const SUBMIT_FAILED_MESSAGE = 'Failed to submit application.';

const CREATE_FAILED_TIN_REGISTERED_MESSAGE =
  'Failed to update application. TIN is already registered.';
const UPDATE_FAILED_TIN_REGISTERED_MESSAGE =
  'Failed to create application. TIN is already registered.';
const SUBMIT_FAILED_TIN_REGISTERED_MESSAGE =
  'Failed to submit application. TIN is already registered.';

const handleError = (
  error: Error,
  messages: {
    general: string;
    tinError: string;
  }
) => {
  if (
    isHttpError(error) &&
    error.code === APPLICATION_ERROR_TIN_ALREADY_REGISTERED
  ) {
    toast(formatHttpErrorMessage(messages.tinError, error), { type: 'error' });
    return;
  }

  toast(formatHttpErrorMessage(messages.general, error), { type: 'error' });
};

export const useApplicationActions = (): UseApplicationActionsValue => {
  const actorRef = PartnerApplicationMachineContext.useActorRef();
  const { confirm } = useConfirmation();
  const queryClient = useQueryClient();

  const createMutation = useCreatePartnerApplication();
  const updateMutation = useUpdatePartnerApplication();
  const submitMutation = useSubmitPartnerApplication();

  const invalidateApplicationsQueries = useCallback(async () => {
    await queryClient.resetQueries({
      queryKey: partnerApplicationKeys.byPerson(),
    });
  }, [queryClient]);

  const updateApplication: UpdateApplicationFn = useCallback(
    async (params) => {
      const context = actorRef.getSnapshot().context;
      const applicationId = context.applicationId;

      if (!applicationId) {
        throw new Error(
          'Cannot update application: applicationId is missing. Create an application first.'
        );
      }

      const firstInvalidSection = validateAndGetFirstInvalidSection(
        context,
        true
      );

      if (firstInvalidSection) {
        await confirm({
          ...validationRedirectMessage,
          acceptOnly: true,
        });
        actorRef.send(getRedirectEventForSection(firstInvalidSection));
        return Promise.reject(createSilentError('APPLICATION_IS_MISSING_DATA'));
      }

      const applicationData =
        mapPartnerApplicationContextToApplicationData(context);

      return updateMutation.mutateAsync(
        {
          applicationId,
          ...applicationData,
        },
        {
          onError: (error) => {
            ampli.mpPaUpdateApplicationFailed();
            params?.onError?.();
            handleError(error, {
              general: UPDATE_FAILED_MESSAGE,
              tinError: UPDATE_FAILED_TIN_REGISTERED_MESSAGE,
            });
          },
        }
      );
    },
    [actorRef, confirm, updateMutation]
  );

  const createOrUpdateApplication = useCallback(async () => {
    const context = actorRef.getSnapshot().context;

    if (context.applicationId) {
      return updateApplication();
    }

    const data = mapPartnerApplicationContextToCreateApplicationData(context);

    if (!data) {
      throw new Error('Cannot create application: data is missing.');
    }

    const response = await createMutation.mutateAsync(data, {
      onError: (error) => {
        ampli.mpPaCreateApplicationFailed();
        handleError(error, {
          general: CREATE_FAILED_MESSAGE,
          tinError: CREATE_FAILED_TIN_REGISTERED_MESSAGE,
        });
      },
    });

    actorRef.send({
      type: 'application.created',
      applicationId: response.applicationId,
    });

    await invalidateApplicationsQueries();

    pushApplicationIdToUrl(response.applicationId);

    return response;
  }, [
    actorRef,
    createMutation,
    invalidateApplicationsQueries,
    updateApplication,
  ]);

  const submitApplication = useCallback(async () => {
    const applicationId = actorRef.getSnapshot().context.applicationId;

    if (!applicationId) {
      throw new Error(
        'Cannot submit application: applicationId is missing. Create an application first.'
      );
    }

    const response = await submitMutation.mutateAsync(
      { applicationId },
      {
        onError: (error) => {
          ampli.mpPaSubmitApplicationFailed();
          handleError(error, {
            general: SUBMIT_FAILED_MESSAGE,
            tinError: SUBMIT_FAILED_TIN_REGISTERED_MESSAGE,
          });
        },
      }
    );

    await invalidateApplicationsQueries();

    return response;
  }, [actorRef, invalidateApplicationsQueries, submitMutation]);

  return useMemo(
    () => ({
      createOrUpdateApplication,
      updateApplication,
      submitApplication,
    }),
    [createOrUpdateApplication, updateApplication, submitApplication]
  );
};
